import { useState, useRef } from "react";
import { C } from "../constants/colors";
import { streamOptionsWithLegacy } from "../constants/wasteData";
import { buildAccountCsvTemplate, parseAccountsCsv } from "../utils/wasteAccountsCsv";
import { Card, Btn, SectionTag, TriBtn, labelStyle, inputStyle } from "../components/ui";

const SIZE_UNITS=[["yard","Yard"],["gal","Gal"],["other","Other"]];
const SIZE_UNIT_ABBR={yard:"yd",gal:"gal",other:""};

const emptyDraft={
  name:"",haulerName:"",stream:"MSW",accountNumber:"",notes:"",
  hasVolume:false,hasWeight:false,weightUnit:"tons",
  containerType:"",containerQuantity:"",containerSize:"",sizeUnit:"yard",weeklyPickupFrequency:"",
};

function draftFromAccount(a){
  return{
    name:a.name||"",haulerName:a.haulerName||"",stream:a.stream||"MSW",
    accountNumber:a.accountNumber||"",notes:a.notes||"",
    hasVolume:!!a.invoiceFormat?.hasVolume,
    hasWeight:!!a.invoiceFormat?.hasWeight,
    weightUnit:a.invoiceFormat?.weightUnit||"tons",
    containerType:a.containerType||"",
    containerQuantity:a.containerQuantity??"",
    containerSize:a.containerSize??"",
    sizeUnit:a.sizeUnit||"yard",
    weeklyPickupFrequency:a.weeklyPickupFrequency??"",
  };
}

// e.g. "2x 6yd Dumpster · 3x/week" — makes the list scannable without opening each account.
function fmtContainer(a){
  const bits=[];
  if(a.containerQuantity||a.containerSize||a.containerType){
    const qty=a.containerQuantity?`${a.containerQuantity}x `:"";
    const unitAbbr=SIZE_UNIT_ABBR[a.sizeUnit]||"";
    const size=a.containerSize?`${a.containerSize}${unitAbbr} `:"";
    const combined=`${qty}${size}${a.containerType||""}`.trim();
    if(combined) bits.push(combined);
  }
  if(a.weeklyPickupFrequency) bits.push(`${a.weeklyPickupFrequency}x/week`);
  return bits.length?bits.join(" · "):null;
}

function downloadTemplate(){
  const csv=buildAccountCsvTemplate();
  const blob=new Blob([csv],{type:"text/csv"});
  const url=URL.createObjectURL(blob);
  const a=document.createElement("a");
  a.href=url; a.download="waste_accounts_template.csv";
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export default function WasteAccounts({accounts,setScreen}){
  const {wasteAccounts,addAccount,addAccountsBulk,updateAccount,removeAccount}=accounts;
  const [editingId,setEditingId]=useState(null); // null=list, "new"=creating, id=editing existing
  const [draft,setDraft]=useState(emptyDraft);
  const [bulkMode,setBulkMode]=useState(false);
  const [bulkFileName,setBulkFileName]=useState("");
  const [bulkPreview,setBulkPreview]=useState(null); // null = not parsed yet; else {rows,headerError}
  const [bulkResultMessage,setBulkResultMessage]=useState("");
  const bulkFileRef=useRef();

  const startNew=()=>{setDraft(emptyDraft);setEditingId("new");setBulkResultMessage("");};
  const startEdit=(a)=>{setDraft(draftFromAccount(a));setEditingId(a.id);setBulkResultMessage("");};
  const cancelEdit=()=>{setEditingId(null);setDraft(emptyDraft);};

  const saveDraft=()=>{
    if(!draft.name.trim()||!draft.haulerName.trim())return;
    const payload={
      name:draft.name.trim(),haulerName:draft.haulerName.trim(),stream:draft.stream,
      accountNumber:draft.accountNumber.trim(),notes:draft.notes.trim(),
      invoiceFormat:{hasVolume:draft.hasVolume,hasWeight:draft.hasWeight,weightUnit:draft.weightUnit},
      containerType:draft.containerType.trim(),
      containerQuantity:draft.containerQuantity===""?null:Number(draft.containerQuantity),
      containerSize:draft.containerSize===""?null:Number(draft.containerSize),
      sizeUnit:draft.sizeUnit,
      weeklyPickupFrequency:draft.weeklyPickupFrequency===""?null:Number(draft.weeklyPickupFrequency),
    };
    if(editingId==="new") addAccount(payload);
    else updateAccount(editingId,payload);
    cancelEdit();
  };

  const fmtFormat=a=>{
    const parts=[];
    if(a.invoiceFormat?.hasVolume) parts.push("Volume (cu yd)");
    if(a.invoiceFormat?.hasWeight) parts.push(`Weight (${a.invoiceFormat.weightUnit==="lbs"?"lbs":"tons"})`);
    return parts.length?parts.join(" · "):"Not configured";
  };

  // ── Bulk CSV setup ────────────────────────────────────────────
  const startBulk=()=>{setBulkMode(true);setBulkFileName("");setBulkPreview(null);setBulkResultMessage("");};
  const cancelBulk=()=>{setBulkMode(false);setBulkFileName("");setBulkPreview(null);};

  const handleBulkFile=async file=>{
    setBulkFileName(file.name);
    const ext=file.name.split(".").pop().toLowerCase();
    if(ext!=="csv"){setBulkPreview({rows:[],headerError:`Please upload a .csv file (got .${ext}).`});return;}
    const text=await file.text();
    setBulkPreview(parseAccountsCsv(text));
  };

  const confirmBulk=()=>{
    if(!bulkPreview||bulkPreview.headerError) return;
    const validAccounts=bulkPreview.rows.filter(r=>!r.error).map(r=>r.account);
    const skipped=bulkPreview.rows.filter(r=>r.error).length;
    if(validAccounts.length>0) addAccountsBulk(validAccounts);
    setBulkResultMessage(`Added ${validAccounts.length} account${validAccounts.length!==1?"s":""}.${skipped>0?` Skipped ${skipped} row${skipped!==1?"s":""} with errors.`:""}`);
    cancelBulk();
  };

  if(bulkMode){
    const validCount=bulkPreview?bulkPreview.rows.filter(r=>!r.error).length:0;
    const errorCount=bulkPreview?bulkPreview.rows.filter(r=>r.error).length:0;
    return(
      <div>
        <SectionTag label="Waste Accounts"/>
        <h2 style={{fontSize:22,fontWeight:800,color:C.navy,margin:"10px 0 6px"}}>Bulk Setup via CSV</h2>
        <p style={{color:C.muted,marginBottom:24,fontSize:14,lineHeight:1.6}}>Set up multiple waste accounts at once — one row per account.</p>

        <Card style={{marginBottom:16}}>
          <div style={{fontWeight:700,color:C.navy,fontSize:14,marginBottom:4}}>1. Download the Template</div>
          <p style={{fontSize:13,color:C.muted,marginBottom:14,lineHeight:1.5}}>Fill in one row per account, keeping the header row exactly as provided.</p>
          <Btn variant="secondary" onClick={downloadTemplate} style={{fontSize:13}}>⬇ Download CSV Template</Btn>
        </Card>

        <Card style={{marginBottom:16}}>
          <div style={{fontWeight:700,color:C.navy,fontSize:14,marginBottom:4}}>2. Upload Completed CSV</div>
          <p style={{fontSize:13,color:C.muted,marginBottom:14,lineHeight:1.5}}>We'll show a preview before anything is saved.</p>
          <div onClick={()=>bulkFileRef.current.click()}
            style={{border:`2px dashed ${bulkFileName?C.teal:C.border}`,borderRadius:10,padding:"24px",textAlign:"center",cursor:"pointer",background:bulkFileName?C.tealLight:C.sand,transition:"all 0.2s"}}>
            <div style={{fontSize:24,marginBottom:6}}>{bulkFileName?"✅":"📄"}</div>
            <div style={{color:bulkFileName?C.teal:C.muted,fontWeight:600,fontSize:13}}>{bulkFileName||"Click to upload CSV"}</div>
          </div>
          <input ref={bulkFileRef} type="file" accept=".csv" style={{display:"none"}}
            onChange={e=>{if(e.target.files[0])handleBulkFile(e.target.files[0]);e.target.value="";}}/>
        </Card>

        {bulkPreview&&bulkPreview.headerError&&(
          <Card style={{marginBottom:16,background:C.redLight,border:`1px solid ${C.red}33`}}>
            <p style={{fontSize:13,color:C.text,margin:0}}>⚠ {bulkPreview.headerError}</p>
          </Card>
        )}

        {bulkPreview&&!bulkPreview.headerError&&(
          <Card style={{marginBottom:16}}>
            <div style={{fontWeight:700,color:C.navy,fontSize:14,marginBottom:4}}>3. Review</div>
            <p style={{fontSize:13,color:C.muted,marginBottom:14}}>
              <strong style={{color:C.green}}>{validCount} valid</strong>{errorCount>0&&<> · <strong style={{color:C.red}}>{errorCount} with errors</strong></>}
            </p>
            <div style={{overflowX:"auto"}}>
              <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
                <thead>
                  <tr style={{borderBottom:`2px solid ${C.border}`}}>
                    <th style={{textAlign:"left",padding:"6px 8px",color:C.muted,fontWeight:700,fontSize:10}}>ROW</th>
                    <th style={{textAlign:"left",padding:"6px 8px",color:C.muted,fontWeight:700,fontSize:10}}>ACCOUNT</th>
                    <th style={{textAlign:"left",padding:"6px 8px",color:C.muted,fontWeight:700,fontSize:10}}>STREAM</th>
                    <th style={{textAlign:"left",padding:"6px 8px",color:C.muted,fontWeight:700,fontSize:10}}>HAULER</th>
                    <th style={{textAlign:"left",padding:"6px 8px",color:C.muted,fontWeight:700,fontSize:10}}>CONTAINER</th>
                  </tr>
                </thead>
                <tbody>
                  {bulkPreview.rows.map(r=>(
                    <tr key={r.rowNum} style={{borderBottom:`1px solid ${C.border}`,background:r.error?C.redLight:C.white}}>
                      <td style={{padding:"6px 8px",color:C.muted}}>{r.rowNum}</td>
                      {r.error?(
                        <td colSpan={4} style={{padding:"6px 8px",color:C.red}}>⚠ {r.error}</td>
                      ):(
                        <>
                          <td style={{padding:"6px 8px",fontWeight:600,color:C.navy}}>{r.account.name}</td>
                          <td style={{padding:"6px 8px",color:C.text}}>{r.account.stream}</td>
                          <td style={{padding:"6px 8px",color:C.text}}>{r.account.haulerName||"—"}</td>
                          <td style={{padding:"6px 8px",color:C.text}}>{fmtContainer(r.account)||"—"}</td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}

        <div style={{display:"flex",justifyContent:"space-between"}}>
          <Btn variant="ghost" onClick={cancelBulk}>← Cancel</Btn>
          <Btn onClick={confirmBulk} disabled={!bulkPreview||!!bulkPreview.headerError||validCount===0}>
            {bulkPreview&&errorCount>0?`Add ${validCount} Account${validCount!==1?"s":""} (skip ${errorCount})`:`Add ${validCount} Account${validCount!==1?"s":""}`}
          </Btn>
        </div>
      </div>
    );
  }

  // ── Add / Edit form ─────────────────────────────────────────
  if(editingId){
    return(
      <div>
        <SectionTag label="Waste Accounts"/>
        <h2 style={{fontSize:22,fontWeight:800,color:C.navy,margin:"10px 0 6px"}}>{editingId==="new"?"Add Waste Account":"Edit Waste Account"}</h2>
        <p style={{color:C.muted,marginBottom:24,fontSize:14,lineHeight:1.6}}>An account represents one hauler line item you receive invoices for — for example one location's MSW pickup or a separate cardboard service.</p>

        <Card style={{marginBottom:16}}>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"16px 24px",marginBottom:16}}>
            <div>
              <label style={labelStyle}>Account Name</label>
              <input style={inputStyle} placeholder="e.g. Main Lodge MSW" value={draft.name} onChange={e=>setDraft({...draft,name:e.target.value})} autoFocus/>
            </div>
            <div>
              <label style={labelStyle}>Hauler Name</label>
              <input style={inputStyle} placeholder="e.g. Waste Connections" value={draft.haulerName} onChange={e=>setDraft({...draft,haulerName:e.target.value})}/>
            </div>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"16px 24px"}}>
            <div>
              <label style={labelStyle}>Stream</label>
              <select style={inputStyle} value={draft.stream} onChange={e=>setDraft({...draft,stream:e.target.value})}>
                {streamOptionsWithLegacy(draft.stream).map(s=><option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Account Number (optional)</label>
              <input style={inputStyle} placeholder="Hauler account #" value={draft.accountNumber} onChange={e=>setDraft({...draft,accountNumber:e.target.value})}/>
            </div>
          </div>
        </Card>

        <Card style={{marginBottom:16,borderLeft:`4px solid ${C.purple}`}}>
          <div style={{fontWeight:700,color:C.navy,fontSize:14,marginBottom:4}}>Container (optional)</div>
          <p style={{fontSize:13,color:C.muted,marginBottom:16,lineHeight:1.5}}>Helps keep your account list scannable — not required.</p>
          <div style={{display:"grid",gridTemplateColumns:"2fr 1fr 1fr 1fr",gap:"16px 16px",marginBottom:16}}>
            <div>
              <label style={labelStyle}>Container Type</label>
              <input style={inputStyle} placeholder="e.g. Dumpster" value={draft.containerType} onChange={e=>setDraft({...draft,containerType:e.target.value})}/>
            </div>
            <div>
              <label style={labelStyle}>Quantity</label>
              <input type="number" min="0" style={inputStyle} placeholder="2" value={draft.containerQuantity} onChange={e=>setDraft({...draft,containerQuantity:e.target.value})}/>
            </div>
            <div>
              <label style={labelStyle}>Size</label>
              <input type="number" min="0" style={inputStyle} placeholder="6" value={draft.containerSize} onChange={e=>setDraft({...draft,containerSize:e.target.value})}/>
            </div>
            <div>
              <label style={labelStyle}>Size Unit</label>
              <select style={inputStyle} value={draft.sizeUnit} onChange={e=>setDraft({...draft,sizeUnit:e.target.value})}>
                {SIZE_UNITS.map(([v,lbl])=><option key={v} value={v}>{lbl}</option>)}
              </select>
            </div>
          </div>
          <div style={{maxWidth:220}}>
            <label style={labelStyle}>Weekly Pickup Frequency</label>
            <input type="number" min="0" style={inputStyle} placeholder="3" value={draft.weeklyPickupFrequency} onChange={e=>setDraft({...draft,weeklyPickupFrequency:e.target.value})}/>
          </div>
        </Card>

        <Card style={{marginBottom:16,borderLeft:`4px solid ${C.teal}`}}>
          <div style={{fontWeight:700,color:C.navy,fontSize:14,marginBottom:4}}>Invoice Format</div>
          <p style={{fontSize:13,color:C.muted,marginBottom:16,lineHeight:1.5}}>What this account's invoices report, so parsed data lines up correctly with your historical records.</p>
          <div style={{marginBottom:16}}>
            <div style={{fontSize:13,color:C.text,marginBottom:7}}>Do invoices include volume?</div>
            <TriBtn options={[["yes","Yes — cubic yards"],["no","No"]]} value={draft.hasVolume?"yes":"no"} onChange={v=>setDraft({...draft,hasVolume:v==="yes"})}/>
          </div>
          <div>
            <div style={{fontSize:13,color:C.text,marginBottom:7}}>Do invoices include weight?</div>
            <TriBtn options={[["yes","Yes"],["no","No"]]} value={draft.hasWeight?"yes":"no"} onChange={v=>setDraft({...draft,hasWeight:v==="yes"})}/>
            {draft.hasWeight&&(
              <div style={{marginTop:10}}>
                <div style={{fontSize:12,color:C.muted,marginBottom:6}}>Weight unit</div>
                <TriBtn options={[["tons","Tons"],["lbs","Lbs"]]} value={draft.weightUnit} onChange={v=>setDraft({...draft,weightUnit:v})}/>
              </div>
            )}
          </div>
        </Card>

        <Card style={{marginBottom:24}}>
          <label style={labelStyle}>Notes (optional)</label>
          <textarea value={draft.notes} onChange={e=>setDraft({...draft,notes:e.target.value})} placeholder="Anything else worth remembering about this account..."
            style={{width:"100%",minHeight:80,padding:12,borderRadius:8,border:`1px solid ${C.border}`,fontSize:13,color:C.text,resize:"vertical",fontFamily:"inherit",boxSizing:"border-box"}}/>
        </Card>

        <div style={{display:"flex",justifyContent:"space-between"}}>
          <Btn variant="ghost" onClick={cancelEdit}>← Cancel</Btn>
          <Btn onClick={saveDraft} disabled={!draft.name.trim()||!draft.haulerName.trim()}>Save Account</Btn>
        </div>
      </div>
    );
  }

  // ── List view ───────────────────────────────────────────────
  return(
    <div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:20,flexWrap:"wrap",gap:12}}>
        <div>
          <SectionTag label="Waste Accounts"/>
          <h2 style={{fontSize:22,fontWeight:800,color:C.navy,margin:"10px 0 4px"}}>Waste Accounts</h2>
          <p style={{color:C.muted,fontSize:13}}>One account per hauler line item — used to recognize invoice charges and build historical waste data automatically.</p>
        </div>
        <div style={{display:"flex",gap:8,flexShrink:0}}>
          <Btn variant="secondary" onClick={startBulk}>⬆ Bulk Setup via CSV</Btn>
          <Btn onClick={startNew}>+ Add Account</Btn>
        </div>
      </div>

      {bulkResultMessage&&(
        <Card style={{marginBottom:20,background:C.greenLight,border:`1px solid ${C.green}33`}}>
          <div style={{fontWeight:600,color:C.green,fontSize:13}}>✓ {bulkResultMessage}</div>
        </Card>
      )}

      {wasteAccounts.length===0?(
        <Card style={{textAlign:"center",padding:"48px 24px",border:`2px dashed ${C.border}`}}>
          <div style={{fontSize:32,marginBottom:12}}>🗂️</div>
          <div style={{fontWeight:700,color:C.navy,fontSize:16,marginBottom:8}}>No waste accounts yet</div>
          <p style={{color:C.muted,fontSize:13,marginBottom:20,lineHeight:1.6,maxWidth:440,margin:"0 auto 20px"}}>
            Add an account for each hauler line item you're billed for — one at a time, or all at once via CSV. Once set up, invoice uploads can be tied to an account so charges are recognized automatically and rolled into your historical waste data.
          </p>
          <div style={{display:"flex",gap:10,justifyContent:"center"}}>
            <Btn variant="secondary" onClick={startBulk}>⬆ Bulk Setup via CSV</Btn>
            <Btn onClick={startNew}>+ Add Account</Btn>
          </div>
        </Card>
      ):(
        wasteAccounts.map(a=>(
          <Card key={a.id} style={{marginBottom:12}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
              <div style={{flex:1,minWidth:0}}>
                <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:4,flexWrap:"wrap"}}>
                  <span style={{fontWeight:700,color:C.navy,fontSize:15}}>{a.name}</span>
                  <span style={{fontSize:11,background:C.tealLight,color:C.teal,fontWeight:700,padding:"2px 8px",borderRadius:10}}>{a.stream}</span>
                </div>
                <div style={{fontSize:12,color:C.muted,marginBottom:6}}>
                  {a.haulerName||"No hauler set"}{a.accountNumber?` · Acct #${a.accountNumber}`:""}
                </div>
                {fmtContainer(a)&&<div style={{fontSize:12,color:C.text,marginBottom:4}}>{fmtContainer(a)}</div>}
                <div style={{fontSize:12,color:C.text}}>
                  {fmtFormat(a)} · {(a.lineItemMappings||[]).length} learned line item{(a.lineItemMappings||[]).length!==1?"s":""}
                </div>
                {a.notes&&<div style={{fontSize:12,color:C.muted,marginTop:6,lineHeight:1.5}}>{a.notes}</div>}
              </div>
              <div style={{display:"flex",gap:8,flexShrink:0}}>
                <Btn variant="secondary" onClick={()=>startEdit(a)} style={{fontSize:12,padding:"6px 14px"}}>Edit</Btn>
                <button onClick={()=>removeAccount(a.id)}
                  style={{background:"none",border:`1px solid ${C.border}`,borderRadius:6,padding:"6px 10px",cursor:"pointer",color:C.muted,fontSize:12,fontWeight:600}}>
                  ✕
                </button>
              </div>
            </div>
          </Card>
        ))
      )}

      <div style={{marginTop:20}}>
        <Btn variant="ghost" onClick={()=>setScreen("waste-hub")}>← Back to Waste Data Hub</Btn>
      </div>
    </div>
  );
}
